import React, { useState, useEffect, useRef, useCallback } from "react"
import Input from "aldoo-ra/Input"
import Button from "aldoo-ra/Button"

const ColorPicker = ({
  initialColor = "#000000",
  onColorSelect,
  onChange,
  onClose,
  onCancel,
  alpha = true,
  showFormatToggle = true,
  showSwatches = true
}) => {
  const [currentColor, setCurrentColor] = useState({ r: 0, g: 0, b: 0, h: 0, s: 0, v: 0, a: 1 })
  const [originalColor, setOriginalColor] = useState('')
  const [format, setFormat] = useState('hex')
  const [inputValue, setInputValue] = useState('')

  const colorAreaRef = useRef(null)
  const colorMarkerRef = useRef(null)
  const hueSliderRef = useRef(null)
  const alphaSliderRef = useRef(null)
  const isDraggingRef = useRef(false)
  const formatRef = useRef(format)
  const alphaRef = useRef(alpha)

  // Keep refs in sync
  useEffect(() => {
    formatRef.current = format
    alphaRef.current = alpha
  }, [format, alpha])

  // Sync input value when currentColor changes (internal update only)
  useEffect(() => {
    // Prevent overwriting user input if it effectively represents the same color
    // This avoids expanding "#f00" to "#ff0000" while typing
    try {
        if (inputValue.startsWith('#')) {
            const currentRgba = hexToRgba(inputValue)
            // tolerance check could be added, but exact match on integer RGB is usually fine
            if (currentRgba.r === currentColor.r && 
                currentRgba.g === currentColor.g && 
                currentRgba.b === currentColor.b && 
                currentRgba.a === currentColor.a) {
                return
            }
        }
    } catch (e) {
        // ignore
    }

    const rgba = { r: currentColor.r, g: currentColor.g, b: currentColor.b, a: currentColor.a }
    const formattedColor = formatColor(rgba, formatRef.current)
    setInputValue(formattedColor)
  }, [currentColor, format])

  // Common color swatches
  const swatches = [
    '#FF0000', '#FF8000', '#FFFF00', '#80FF00', '#00FF00', '#00FF80',
    '#00FFFF', '#0080FF', '#0000FF', '#8000FF', '#FF00FF', '#FF0080',
    '#000000', '#404040', '#808080', '#C0C0C0', '#FFFFFF', '#800000',
    '#804000', '#808000', '#408000', '#008000', '#008040', '#008080',
    '#004080', '#000080', '#400080', '#800080', '#800040'
  ]

  // Convert hex to RGBA
  const hexToRgba = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})?$/i.exec(hex)
    if (!result) return { r: 0, g: 0, b: 0, a: 1 }

    return {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16),
      a: result[4] ? parseInt(result[4], 16) / 255 : 1
    }
  }

  // Convert RGBA to HSV
  const rgbaToHsv = (r, g, b, a) => {
    r /= 255; g /= 255; b /= 255

    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    const diff = max - min

    let h = 0
    if (diff !== 0) {
      if (max === r) h = ((g - b) / diff) % 6
      else if (max === g) h = (b - r) / diff + 2
      else h = (r - g) / diff + 4
    }
    h = Math.round(h * 60)
    if (h < 0) h += 360

    const s = max === 0 ? 0 : Math.round((diff / max) * 100)
    const v = Math.round(max * 100)

    return { h, s, v, a, r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255) }
  }

  // Convert HSV to RGBA
  const hsvToRgba = (h, s, v, a) => {
    s /= 100; v /= 100

    const c = v * s
    const x = c * (1 - Math.abs((h / 60) % 2 - 1))
    const m = v - c

    let r, g, b
    if (h >= 0 && h < 60) { r = c; g = x; b = 0 }
    else if (h >= 60 && h < 120) { r = x; g = c; b = 0 }
    else if (h >= 120 && h < 180) { r = 0; g = c; b = x }
    else if (h >= 180 && h < 240) { r = 0; g = x; b = c }
    else if (h >= 240 && h < 300) { r = x; g = 0; b = c }
    else { r = c; g = 0; b = x }

    return {
      r: Math.round((r + m) * 255),
      g: Math.round((g + m) * 255),
      b: Math.round((b + m) * 255),
      a
    }
  }

  // Convert RGBA to various formats
  const formatColor = (color, fmt = format) => {
    const { r, g, b, a } = color

    switch (fmt) {
      case 'hex':
        const hex = ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)
        return alpha && a < 1 ? `#${hex}${Math.round(a * 255).toString(16).padStart(2, '0')}` : `#${hex}`
      case 'rgb':
        return alpha && a < 1 ? `rgba(${r}, ${g}, ${b}, ${a.toFixed(2)})` : `rgb(${r}, ${g}, ${b})`
      case 'hsl':
        const hsl = rgbToHsl(r, g, b)
        return alpha && a < 1 ? `hsla(${hsl.h}, ${hsl.s}%, ${hsl.l}%, ${a.toFixed(2)})` : `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)`
      default:
        return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`
    }
  }

  // Convert RGB to HSL for HSL format output
  const rgbToHsl = (r, g, b) => {
    r /= 255; g /= 255; b /= 255
    const max = Math.max(r, g, b), min = Math.min(r, g, b)
    let h, s, l = (max + min) / 2

    if (max === min) {
      h = s = 0
    } else {
      const d = max - min
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break
        case g: h = (b - r) / d + 2; break
        case b: h = (r - g) / d + 4; break
      }
      h /= 6
    }

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100)
    }
  }

  // Initialize color from initial value
  useEffect(() => {
    if (initialColor) {
      let hsv = { r: 0, g: 0, b: 0, h: 0, s: 0, v: 0, a: 1 }
      let parsed = false
      
      if (initialColor.startsWith('#')) {
         const rgba = hexToRgba(initialColor)
         hsv = rgbaToHsv(rgba.r, rgba.g, rgba.b, rgba.a)
         parsed = true
      } else if (initialColor.startsWith('rgb') || initialColor.startsWith('hsl')) {
          // Add simplistic parsing or just default to black for complex strings we don't handle yet
          // For now, let's assume if it's not hex, we can't visualize it on the picker easily
          // so we leave the picker at default (black) or previous color
      }

      // Update local state, but DO NOT fire onChange
      if (parsed) {
        setCurrentColor(hsv)
      }
      
      // Always respect the text value passed in, even if we can't visualize it
      setInputValue(initialColor)
      setOriginalColor(initialColor) 
    }
  }, [initialColor, alpha])

  // Handle color area click/drag
  const handleColorAreaInteraction = useCallback((event) => {
    if (!colorAreaRef.current) return

    const rect = colorAreaRef.current.getBoundingClientRect()
    const x = Math.max(0, Math.min(event.clientX - rect.left, rect.width))
    const y = Math.max(0, Math.min(event.clientY - rect.top, rect.height))

    const s = (x / rect.width) * 100
    const v = 100 - (y / rect.height) * 100

    setCurrentColor(prevColor => {
      const newColor = { ...prevColor, s, v }
      const rgba = hsvToRgba(newColor.h, newColor.s, newColor.v, newColor.a)
      const updatedColor = { ...newColor, ...rgba }
      
      const formatted = formatColor({ r: updatedColor.r, g: updatedColor.g, b: updatedColor.b, a: updatedColor.a }, formatRef.current)
      
      // Defer parent update to avoid "setState during render" error
      setTimeout(() => onChange?.(formatted), 0)
      
      return updatedColor
    })
  }, [onChange])

  // Handle hue change
  const handleHueChange = (event) => {
    const h = parseInt(event.target.value)
    const newColor = { ...currentColor, h }
    const rgba = hsvToRgba(newColor.h, newColor.s, newColor.v, newColor.a)
    const updatedColor = { ...newColor, ...rgba }

    setCurrentColor(updatedColor)
    const formattedColor = formatColor(rgba, formatRef.current)
    setInputValue(formattedColor)
    setTimeout(() => onChange?.(formattedColor), 0)
  }

  // Handle alpha change
  const handleAlphaChange = (event) => {
    const a = parseFloat(event.target.value) / 100
    const newColor = { ...currentColor, a }
    const rgba = { r: currentColor.r, g: currentColor.g, b: currentColor.b, a }

    setCurrentColor(newColor)
    const formattedColor = formatColor(rgba, formatRef.current)
    setInputValue(formattedColor)
    setTimeout(() => onChange?.(formattedColor), 0)
  }

  // Handle input change
  const handleInputChange = (event) => {
    const value = event.target.value
    setInputValue(value) // Allow free typing

    // Always notify parent of the raw string (e.g. "primary", "transparent", invalid hex)
    setTimeout(() => onChange?.(value), 0)

    // Try to parse as color to update the picker UI
    try {
      if (value.startsWith('#')) {
         const rgba = hexToRgba(value)
         const hsv = rgbaToHsv(rgba.r, rgba.g, rgba.b, rgba.a)
         setCurrentColor(hsv)
      }
      // Add other parsers here if needed, but for now just prevent crashing on invalid
    } catch (e) {
      // Invalid color for picker visualization, but valid for input
    }
  }

  // Handle swatch click
  const handleSwatchClick = (color) => {
    const rgba = hexToRgba(color)
    const hsv = rgbaToHsv(rgba.r, rgba.g, rgba.b, rgba.a)
    setCurrentColor(hsv)
    const formattedColor = formatColor(rgba, formatRef.current)
    setInputValue(formattedColor)
    setTimeout(() => onChange?.(formattedColor), 0)
  }

  // Handle format change
  const handleFormatChange = (newFormat) => {
    setFormat(newFormat)
    const rgba = { r: currentColor.r, g: currentColor.g, b: currentColor.b, a: currentColor.a }
    setInputValue(formatColor(rgba, newFormat))
  }

  // Handle color selection
  const handleSelect = () => {
    onColorSelect?.(inputValue)
  }

  // Handle cancel - restore original color
  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    } else if (originalColor) {
      onChange?.(originalColor) // Restore original color via onChange
    }
    onClose?.()
  }

  // Get current hue color for background
  const hueColor = `hsl(${currentColor.h}, 100%, 50%)`

  return (
    <div className="w-96 bg-white rounded-lg shadow-xl border border-gray-200 p-4">
      <div className="flex gap-4">
        {/* Left Column - Color Picker */}
        <div className="flex-1 space-y-3">
          {/* Color Area */}
          <div className="relative h-32 rounded-md overflow-hidden cursor-crosshair"
           style={{
             backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,1)), linear-gradient(to right, rgba(255,255,255,1), ${hueColor})`
           }}
           ref={colorAreaRef}
           onMouseDown={(e) => {
             isDraggingRef.current = true
             handleColorAreaInteraction(e)
           }}
           onMouseMove={(e) => {
             if (isDraggingRef.current) handleColorAreaInteraction(e)
           }}
           onMouseUp={() => isDraggingRef.current = false}
           onMouseLeave={() => isDraggingRef.current = false}>

        {/* Color Marker */}
        <div
          ref={colorMarkerRef}
          className="absolute w-4 h-4 border-2 border-white rounded-full shadow-sm pointer-events-none transform -translate-x-2 -translate-y-2"
          style={{
            left: `${currentColor.s}%`,
            top: `${100 - currentColor.v}%`,
            backgroundColor: formatColor({ r: currentColor.r, g: currentColor.g, b: currentColor.b, a: 1 }, 'hex')
          }}
        />
      </div>

          {/* Hue Slider */}
          <div className="relative">
            <div className="text-gray-600 text-xs mb-1">Hue</div>
            <div className="relative h-3 rounded-md overflow-hidden"
                 style={{
                   backgroundImage: 'linear-gradient(to right, #ff0000 0%, #ffff00 16.66%, #00ff00 33.33%, #00ffff 50%, #0000ff 66.66%, #ff00ff 83.33%, #ff0000 100%)'
                 }}>
              <input
                ref={hueSliderRef}
                type="range"
                min="0"
                max="360"
                value={currentColor.h}
                onChange={handleHueChange}
                className="absolute w-full h-full opacity-0 cursor-pointer"
              />
              <div
                className="absolute w-3 h-3 border-2 border-white rounded-full shadow-sm pointer-events-none transform -translate-x-1.5 -translate-y-0"
                style={{
                  left: `${(currentColor.h / 360) * 100}%`,
                  top: '0',
                  backgroundColor: hueColor
                }}
              />
            </div>
          </div>

          {/* Alpha Slider */}
          {alpha && (
            <div className="relative">
              <div className="text-gray-600 text-xs mb-1">Opacity</div>
              <div className="relative h-3 rounded-md overflow-hidden"
                   style={{
                     backgroundImage: `linear-gradient(to right, transparent, ${formatColor({ r: currentColor.r, g: currentColor.g, b: currentColor.b, a: 1 }, 'hex')}), repeating-linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc), repeating-linear-gradient(45deg, #ccc 25%, #fff 25%, #fff 75%, #ccc 75%, #ccc)`,
                     backgroundPosition: '0 0, 2px 2px',
                     backgroundSize: '100% 100%, 4px 4px, 4px 4px'
                   }}>
                <input
                  ref={alphaSliderRef}
                  type="range"
                  min="0"
                  max="100"
                  value={currentColor.a * 100}
                  onChange={handleAlphaChange}
                  className="absolute w-full h-full opacity-0 cursor-pointer"
                />
                <div
                  className="absolute w-3 h-3 border-2 border-white rounded-full shadow-sm pointer-events-none transform -translate-x-1.5 -translate-y-0"
                  style={{
                    left: `${currentColor.a * 100}%`,
                    top: '0',
                    backgroundColor: formatColor({ r: currentColor.r, g: currentColor.g, b: currentColor.b, a: currentColor.a }, 'hex')
                  }}
                />
              </div>
            </div>
          )}

          {/* Format Toggle */}
          {showFormatToggle && (
            <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
              {['hex', 'rgb', 'hsl'].map((fmt) => (
                <button
                  key={fmt}
                  onClick={() => handleFormatChange(fmt)}
                  className={`flex-1 px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                    format === fmt
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {fmt.toUpperCase()}
                </button>
              ))}
            </div>
          )}

          {/* Color Input */}
          <Input
            variant="admin"
            value={inputValue}
            onChange={handleInputChange}
            className="text-center font-mono text-sm"
            placeholder="Enter color value"
          />
        </div>

        {/* Right Column - Color Swatches */}
        {showSwatches && (
          <div className="w-32">
            <div className="text-gray-600 text-xs mb-2">Swatches</div>
            <div className="grid grid-cols-6 gap-1">
              {swatches.map((swatch, index) => (
                <button
                  key={index}
                  onClick={() => handleSwatchClick(swatch)}
                  className="w-5 h-5 rounded border border-gray-300 hover:border-gray-400 transition-colors"
                  style={{ backgroundColor: swatch }}
                  title={swatch}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-3 mt-3 border-t border-gray-200">
        <Button
          variant="admin_secondary"
          onClick={handleCancel}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          variant="admin_primary"
          onClick={handleSelect}
          className="flex-1"
        >
          Select
        </Button>
      </div>
    </div>
  )
}

export default ColorPicker